import {
  Button,
  ColumnTable,
  Icon,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { formatDate } from '../../../../utils/dates';
import { deleteAdmissionProcessAction } from '../parts/actions';

export const useColumns = (refresh?: () => void | Promise<void>) => {
  const { t } = useTranslation('academic-offer/admission-processes');
  const history = useHistory();

  const columns = useMemo<ColumnTable<any>[]>(
    () => [
      {
        headerText: t('table.processName'),
        thClassName: 'text-left pl-5',
        tdClassName: 'text-left pl-5',
        width: '300px',
        columnName: 'name',
        cellFormat: ({ row }: any) => `${row.process?.name}`,
      },
      {
        headerText: t('table.processType'),
        thClassName: 'text-center',
        columnName: 'processType',
        cellFormat: ({ row }: any) => row.process?.tuitionProcessType?.name,
      },
      {
        headerText: t('table.academicPeriod'),
        thClassName: 'text-center',
        columnName: 'period',
        cellFormat: ({ row }: any) => row.process?.academicOffer?.period?.name,
      },
      {
        headerText: t('table.startDate'),
        thClassName: 'text-center',
        columnName: 'startDate',
        cellFormat: ({ row }: any) =>
          row.process?.startedAt
            ? formatDate(row.process.startedAt, 'DD/MM/YYYY')
            : '-',
      },
      {
        headerText: t('table.endDate'),
        thClassName: 'text-center',
        columnName: 'endDate',
        cellFormat: ({ row }: any) =>
          row.process?.endedAt
            ? formatDate(row.process.endedAt, 'DD/MM/YYYY')
            : '-',
      },
      {
        headerText: t('table.multiTuition'),
        thClassName: 'text-center',
        columnName: 'multiTuition',
        cellFormat: ({ row }: any) =>
          row.process?.isMultiTuition ? t('common.yes') : t('common.no'),
      },
      {
        headerText: t('table.quotas'),
        thClassName: 'text-center',
        columnName: 'quotas',
        cellFormat: ({ row }: any) => row.process?.totalQuota || 0,
      },
      {
        headerText: t('table.enrolled'),
        thClassName: 'text-center',
        columnName: 'enrolled',
        cellFormat: ({ row }: any) => row.process?.enrolledCount || 0,
      },
      {
        headerText: t('table.status'),
        thClassName: 'text-center',
        columnName: 'status',
        cellFormat: ({ row }: any) => (
          <div>
            <Badge
              color={row.process?.isEnabled ? 'primary' : 'danger'}
              style={{
                width: '100px',
                paddingRight: '5px',
              }}
              pill
            >
              {row.process?.isEnabled
                ? t('common.enabled')
                : t('common.disabled')}
            </Badge>
          </div>
        ),
      },
      {
        headerText: t('table.manage'),
        thClassName: 'text-center',
        columnName: 'manage',
        cellFormat: ({ row }: any) => (
          <div className="d-flex justify-content-center align-items-center">
            <Button
              text={t('table.manage')}
              size="sm"
              className="mr-2"
              onClick={() => {
                history.push(
                  `/academic-offer/admission-processes/${row.process?.id}`,
                );
              }}
            />
            <button
              aria-label={t('table.delete')}
              type="button"
              className="btn btn-sm"
              onClick={() => {
                showDialogConfirm({
                  icon: { name: 'warning', color: 'warning' },
                  title: t('table.deleteConfirm.title'),
                  subtitle: t('table.deleteConfirm.subtitle'),
                  btnConfirm: {
                    text: t('table.deleteConfirm.confirm'),
                    onConfirm: async () => {
                      const result = await deleteAdmissionProcessAction({
                        processId: row.process?.id,
                      });
                      // `result.success`, no `result`: la action siempre
                      // resuelve a un objeto (`{success:true,data}` o
                      // `{success:false,data}`), así que el truthiness daba
                      // SIEMPRE true. El toast salía verde incluso con un 500 o
                      // con el 400 de "no se puede eliminar un proceso con
                      // postulaciones", y `deleteError` era código muerto.
                      addToast({
                        text: result.success
                          ? t('table.deleteSuccess')
                          : t('table.deleteError'),
                        color: result.success ? 'success' : 'danger',
                      });
                      if (result.success) {
                        await refresh?.();
                      }
                    },
                  },
                  btnClose: { text: t('table.deleteConfirm.cancel') },
                });
              }}
            >
              <Icon name="trash" color="danger" />
            </button>
          </div>
        ),
      },
    ],
    [t, history, refresh],
  );

  return columns;
};
